import cx from 'classnames';
import { ChangeEvent, FC, ReactNode, useMemo, useState } from 'react';

import { withStyles, WithStyles } from '@core/theme/utils/with-styles';
import { Flex } from '@shared/components/flex';
import { TextField, TextFieldProps } from '@shared/components/text-field';

import { styles } from './LocalLookup.styles';

export interface LocalLookupProps extends WithStyles<typeof styles> {
  dropUp?: boolean;
  inputProps?: Omit<TextFieldProps, 'classes'>;
  label?: ReactNode | string;
  optionsList: any[];
  renderOption: (option: any) => JSX.Element | string;
  searchValue: string;
  withClearIcon?: boolean;
  withSearchIcon?: boolean;
  onSearchValueChange: (search: string) => void;
  onSelect: (item: any) => void;
}

const LocalLookupComponent: FC<LocalLookupProps> = ({
  classes,
  dropUp = false,
  inputProps,
  label,
  optionsList,
  renderOption,
  searchValue,
  withClearIcon = false,
  withSearchIcon = false,
  onSearchValueChange,
  onSelect,
}) => {
  const [open, setOpen] = useState(false);

  const handleSearchChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    onSearchValueChange(value);
  };

  const handleTextFieldBlur = () => {
    setTimeout(() => {
      setOpen(false);
    }, 200);
  };

  const handleClick = (el: any) => {
    onSelect(el);
    setOpen(false);
  };

  const listOptionsContent = useMemo(() => {
    if (!optionsList.length) {
      return null;
    }

    return optionsList.map((item, key) => {
      return (
        <div onClick={() => handleClick(item)} className={classes.listItem} key={key}>
          {renderOption(item)}
        </div>
      );
    });
  }, [classes.listItem, handleClick, optionsList, renderOption]);

  const listContent = useMemo(() => {
    if (open && listOptionsContent) {
      return (
        <Flex
          autoWidth={false}
          direction="column"
          wrap="nowrap"
          className={cx(classes.list, { [classes.listAbove]: dropUp })}
        >
          <div className={classes.optionsList}>{listOptionsContent}</div>
        </Flex>
      );
    }

    return null;
  }, [classes, handleClick, listOptionsContent, open, renderOption, searchValue, withClearIcon]);

  return (
    <div className={classes.root}>
      <Flex autoWidth={false} justifyContent="flex-start">
        {label && <span className={classes.label}>{label}</span>}
      </Flex>
      <div className={classes.search}>
        <TextField
          {...inputProps}
          fullWidth
          InputProps={{
            ...inputProps?.InputProps,
          }}
          withSearchIcon={withSearchIcon}
          value={searchValue}
          onChange={handleSearchChange}
          onBlur={handleTextFieldBlur}
          onFocus={() => setOpen(true)}
          classes={{
            root: classes.textField,
            input: classes.textFieldInput,
          }}
        />
        {listContent}
      </div>
    </div>
  );
};

export const LocalLookup = withStyles(styles)(LocalLookupComponent);
